<template>
  <div
    ref="$el"
    class="tabs-with-image"
  >
    <div class="container">
      <UiComponentHeading
        :level="2"
        class="title"
      >
        {{ title }}
      </UiComponentHeading>
      <!-- TABS -->
      <div
        ref="tabs"
        class="tabs"
      >
        <button
          v-for="(tab, index) in tabs"
          :key="index"
          class="button"
          :class="{ active: isActiveTab(tab) }"
          button="button"
          @click="onClickTab(index)"
        >
          {{ tab.tabTitle }}
          <span
            class="tab-underline"
            :style="{
              width: isActiveTab(tab) ? '100%' : 0,
            }"
          />
        </button>
      </div>
      <!-- CONTENT -->
      <div
        class="content"
        :style="contentStyle"
      >
        <div
          v-for="(tab, index) in tabs"
          :id="`tab-${tab.id}`"
          :key="`tab-${tab.id}`"
          class="tab"
          :class="{ active: isActiveTab(tab) }"
        >
          <div class="container">
            <UiComponentHeading
              :level="3"
              class="title-tab"
              :class="{
                skinTone: index === 0,
                paleBlue: index === 1,
                carmin: index === 2,
              }"
            >
              {{ tab.subTitle }}
            </UiComponentHeading>
            <OrganismRichText
              :content="[
                {
                  __typename: 'ContentTextHtmlRecord',
                  text: tab.paragraph || '',
                },
              ]"
            />
            <div class="button-list">
              <AtomButton
                v-if="hasPrimaryButton"
                :action="primaryButton.action"
                :icon-position="primaryButton.iconPosition"
                :label="primaryButton.label"
                size="l"
                :to="primaryButton.to"
                @click="emit('primary:click', $event)"
              >
                {{ primaryButton.slot }}
              </AtomButton>
              <AtomButton
                v-if="hasSecondaryButton"
                :action="secondaryButton.action"
                :atttributes="{
                  position: `Tab ${Number(index + 1)}`,
                }"
                :label="secondaryButton.label"
                size="l"
                :to="secondaryButton.to"
                :type="
                  secondaryButton.type ? secondaryButton.type : 'secondary'
                "
                @click="emit('secondary:click', $event)"
              >
                {{ secondaryButton.slot }}
              </AtomButton>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="tabs.filter((tab) => tab.image).length > 0"
      class="media-right"
    >
      <UiComponentImage
        v-for="tab in tabs"
        :key="tab.id"
        :class="{
          active: isActiveTab(tab),
          shadow: tab.image.format === 'jpg',
        }"
        :alt="tab.image && tab.image.alt"
        :height="tab.image.height"
        :source="tab.image && tab.image.url"
        :expected-size="
          screenWidth >= 768
            ? imageHeight[activeTabIndex]
            : imageWidth[activeTabIndex]
        "
        :orientation="screenWidth >= 768 ? 'portrait' : 'landscape'"
        :width="tab.image.width"
      />
    </div>
  </div>
</template>

<script setup>
// Emitter
const emit = defineEmits([
  'primary:click',
  'secondary:click',
])

// Props
const props = defineProps({
  primaryButton: {
    type: Object,
    default: () => ({}),
  },
  tabs: {
    type: Array,
    required: true,
    default: () => [],
  },
  title: {
    type: String,
    required: true,
  },
  secondaryButton: {
    type: Object,
    default: () => ({}),
  },
})

// Refs
const $el = ref(null)
const activeTabIndex = ref(0)
const contentStyle = ref({ height: 'auto' })
const imageHeight = ref([0])
const imageWidth = ref([0])
const screenWidth = ref(360)

// Computed Properties
const hasPrimaryButton = computed(() => {
  return !!(props.primaryButton && props.primaryButton.slot)
})

const hasSecondaryButton = computed(() => {
  return !!(props.secondaryButton && props.secondaryButton.slot)
})

// Methods
function isActiveTab({ id }) {
  return (
    props.tabs.findIndex(tab => tab.id === id) === activeTabIndex.value
  )
}

function onClickTab(index) {
  activeTabIndex.value = index
}

function onResize() {
  const { clientWidth } = document.documentElement
  const contentHeight
    = clientWidth < 1328
      ? 'auto'
      : $el.value.querySelector('.tab').offsetHeight

  contentStyle.value = {
    height: `${contentHeight}px`,
  }
  screenWidth.value = clientWidth
  props.tabs
    .filter(tab => tab.image)
    .forEach(({ image }, index) => {
      switch (true) {
        case clientWidth < 768:
          imageWidth.value[index] = clientWidth - 32
          imageHeight.value[index] = Math.round(
            imageWidth.value[index] / (image.width / image.height),
          )
          break
        default:
          imageHeight.value[index] = 661
          imageWidth.value[index] = Math.round(
            imageHeight.value[index] * (image.width / image.height),
          )
      }
    })
}

// Lifecycle hooks
onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onMounted(() => {
  window.addEventListener('resize', onResize)
  if (props.tabs.filter(tab => tab.image).length > 0) {
    imageHeight.value = props.tabs.map(({ image }) => image.height)
    imageWidth.value = props.tabs.map(({ image }) => image.width)
  }
  onResize()
})
</script>

<style lang="postcss" scoped>
.tabs-with-image {
  @apply relative flex flex-col w-full;

  @screen xl {
    @apply flex-row justify-between;
  }

  > .container {
    @apply flex flex-col;

    @screen xl {
      width: 620px;
    }
  }

  .tabs {
    @apply mt-3 h-16 flex overflow-x-scroll;

    @screen xl {
      @apply flex overflow-x-auto;
    }

    .button {
      min-width: fit-content;
      @apply mr-10 h-full relative outline-none text-gray-600;

      &.active {
        @apply text-gray-900 font-medium;
      }
    }

    .tab-underline {
      @apply absolute bottom-0 left-0 h-1 bg-livestorm-blue-600;
    }
  }

  .content {
    @apply relative mt-8;

    .tab {
      @apply hidden opacity-0 invisible transition-all duration-600 ease-in-out;

      @screen xl {
        @apply flex absolute;
      }

      &.active {
        @apply flex opacity-100 visible transition-all duration-600 delay-200 ease-in-out;
      }
    }

    > .container {
      @apply flex flex-col items-start;

      .h3 {
        @apply text-xl leading-7;

        @screen mdminus {
          @apply text-2xl leading-8;
        }

        @screen mdplus {
          @apply text-2xl leading-8;
        }
      }
    }

    .title-tab {
      @apply text-2xl font-medium;
    }

    .button-list {
      @apply mt-8 flex flex-col;

      @screen md {
        @apply flex-row;
      }

      .button {
        @apply w-full;

        @screen md {
          @apply w-auto;
        }

        &:last-child {
          @apply mt-4;

          @screen md {
            @apply mt-0 ml-6;
          }
        }
      }
    }

    .rich-text {
      @apply mt-4 text-grey-blue-600;

      :deep(p) {
        @apply text-grey-blue-600;
      }
    }
  }

  .media-right {
    max-height: 343px;
    @apply mt-16 w-full;

    @screen md {
      height: 661px;
      max-height: 661px;
    }

    @screen xl {
      width: 620px;
      @apply mt-0 flex;
    }

    picture {
      @apply hidden opacity-0 invisible transition-all duration-600 ease-in-out;
      border-radius: 16px;
      will-change: opacity;

      @screen md {
        @apply rounded-[24px];
      }

      @screen xl {
        @apply flex absolute;
      }

      &.active {
        @apply flex opacity-100 visible transition-all duration-600 ease-in-out;
      }

      &.shadow {
        @apply shadow-light4;
      }

      :deep(img) {
        @screen xl {
          @apply object-left;
        }
      }
    }
  }

  .learn-more {
    @apply p-0 !important;
  }
}
</style>
